import type { ComputedRef, MaybeRef } from 'vue';
import { computed, unref } from 'vue';

export type PaginationItem = number | '...';

/**
 * Builds an array of page numbers for pagination UI, with ellipsis for gaps.
 * @param currentPage - 1-based current page
 * @param totalPages - Total number of pages
 * @returns Array of page numbers and '...' for gaps
 */
export function paginationWithDots(currentPage: number, totalPages: number): PaginationItem[] {
  let delta = 1;
  if (currentPage - delta <= 0) {
    delta = 2;
  }
  if (currentPage + delta > totalPages) {
    delta = 2;
  }
  const left = currentPage - delta;
  const right = currentPage + delta + 1;
  const range: number[] = [];
  const rangeWithDots: PaginationItem[] = [];
  let item: number | undefined;

  for (let i = 1; i <= totalPages; i += 1) {
    if (i === 1 || i === totalPages || (i >= left && i < right)) {
      range.push(i);
    }
  }

  range.forEach((i) => {
    if (item !== undefined) {
      if (i - item !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    item = i;
  });

  return rangeWithDots;
}

/**
 * Composable for pagination page list with dots.
 * @param currentPage - Current page (ref or number)
 * @param totalPages - Total pages (ref or number)
 */
export function usePagination(
  currentPage: MaybeRef<number>,
  totalPages: MaybeRef<number>,
): { paginationWithDots: ComputedRef<PaginationItem[]> } {
  const paginationWithDotsComputed = computed(() =>
    paginationWithDots(unref(currentPage), unref(totalPages)),
  );
  return { paginationWithDots: paginationWithDotsComputed };
}
